import type {
  Control,
  Message,
  DocumentVerwijzing,
  Thread,
  Moment,
  Person,
  RowId,
  Section,
  FieldId,
  SectionId,
} from '@eformulieren/model'
import { Source } from '@eformulieren/model'
import * as Y from 'yjs'
import { isOrderKey, keyBetween } from './orderKey.js'

/**
 * De sectie is het document (ontwerp 6.8).
 *
 * Deze module is de enige plek waar de vorm van een sectiedocument staat:
 *
 * - één `Y.Map` met alle gewone veldwaarden, gekeyd op veld-id;
 * - één `Y.XmlFragment` per rich-text-veld, met de veld-id als naam;
 * - één `Y.Text` per tekstveld (`text` en `textarea`), met de veld-id als naam, zodat twee
 *   mensen die tegelijk in hetzelfde tekstveld typen per teken worden samengevoegd (R-72, zie
 *   {@link MERGED_TEXT_CONTROLS});
 * - één `Y.Array` van `Y.Map` per herhaalgroep, met een stabiel rij-id per rij,
 *   zodat twee mensen aan verschillende rijen kunnen werken zonder elkaar te
 *   overschrijven (R-40 tot R-42), en een volgordesleutel per rij ({@link ROW_ORDER}):
 *   de getoonde volgorde is die van de sleutels en niet die van de lijst.
 *
 * Wat hier wordt vastgelegd is vanaf M3 persistent: de store bewaart deze
 * documenten en interpreteert ze niet (ontwerp 9.1). Een sleutel omdopen is
 * daarna een migratie, geen wijziging.
 */

/**
 * De naam van de waardenmap. Kan niet botsen met een veld-id: die begint met
 * `vld-`, `grp-` of `cmp-` (model, `PREFIX`), en een test houdt dat vast.
 */
export const VALUES = 'waarden'

/**
 * Auteur en moment staan als eigen sleutels naast de waarde in dezelfde map
 * (`vld-x`, `vld-x@door`, `vld-x@op`, ontwerp 4.6). Een CRDT levert dat voor een
 * map niet zelf, en zonder deze twee zijn R-97 (dat en wanneer gewijzigd) en
 * R-188 (de wijziging van een medeschrijver traceerbaar) niet waar te maken.
 *
 * Ook voor een rich-text-veld houdt deze module de herkomst per veld hier bij.
 * Ontwerp 4.6 leidt die voor rich text af uit `Y.PermanentUserData` en het
 * ychange-schema; dat is de route voor de attributie tot op de zin (6.9). De
 * herkomst die een revisie per antwoord vraagt is er één per veld, dus één
 * mechanisme voor alle velden is hier genoeg.
 *
 * **Welke van de twee gezaghebbend is, is met M3.12 beslecht: deze sleutels.** Zij
 * zijn wat de uitgeschreven stand van 4.5 leest, en de pod toetst ze bij elke
 * verwerkte wijziging aan wat het token zegt (`apps/runtime/src/origin.ts`,
 * R-154). Wat een client hier schrijft is dus een bewering die wordt gecorrigeerd,
 * en dat hoort zo: waarde en herkomst gaan in één transactie de map in, zodat een
 * medeschrijver nooit een waarde zonder auteur ziet. De attributie tot op de zin
 * gaat over een andere vraag: welke zin van wie is, niet wie het veld als laatste
 * raakte.
 *
 * **Niet bij elke aanslag (besluit van 29 oktober 2026, ontwerp 4.6).** Elke `set` in
 * een `Y.Map` blijft in het document staan zolang de opruiming uit staat (valkuil 1),
 * en een herkomst per aanslag bleek in de belastingmeting van M8 de grootste groeibron
 * van een sectiedocument. Daarom schrijft de renderer de herkomst alleen als het veld
 * er nog geen heeft of als die van een ander is (`recordOriginIn`); het moment per
 * minuut zet de pod uit zijn eigen klok (`apps/runtime/src/origin.ts`). Een akkoord is
 * de uitzondering: dat schrijft zijn moment bij elke keuze (`exactMoment`, R-19, R-89).
 */
export const BY_SUFFIX = '@door'
export const AT = '@op'

/**
 * De naam van de auteursmap: welke Yjs-client bij welke persoon hoort (ontwerp 6.9).
 *
 * Dit is de map die `Y.PermanentUserData` verwacht, en daarmee wat y-tiptap leest als het
 * een snapshot rendert: per tekstfragment de auteur, en dus de kleur. Yjs koppelt een
 * wijziging aan de client die hem maakte en niet aan een mens; deze map is de brug, en hij
 * reist in het document mee zodat ook een auteur die nu offline is bij zijn zinnen blijft
 * horen.
 *
 * **De pod is de enige die hem vult, uit het token** (`apps/runtime/src/auteurs.ts`,
 * R-154). In de CitatenPOC schrijft de client hier zijn eigen naam, en dan is de attributie
 * een bewering van de partij die er belang bij heeft. Wat een client hier zelf neerzet wordt
 * daarom door de pod overschreven, net als bij de herkomst per veld.
 *
 * Kan niet botsen met een veld-id, om dezelfde reden als {@link VALUES}.
 */
export const AUTHORS = 'auteurs'

/** Wat de auteursmap per persoon draagt: de vorm die `Y.PermanentUserData` leest. */
const IDS = 'ids'
const DELETESETS = 'ds'

/**
 * De naam van de dradenmap: de opmerkingdraadjes bij de velden van deze sectie (R-73, 6.8).
 *
 * In het document en niet in een tabel, want een draadje is samenwerkinhoud die op tekenniveau
 * meebeweegt met de tekst (R-72) en die een revisie meeneemt. De vorm van één draadje staat in
 * het model (`Thread` in `packages/model/src/draad.ts`); wat hier staat is hoe hij in Yjs ligt, en
 * dat is niet hetzelfde: een CRDT vraagt per veld een keuze over wat er gebeurt als twee mensen
 * het tegelijk aanraken.
 *
 * **Per draadje een `Y.Map` en de berichten een `Y.Array`.** Twee medeschrijvers die tegelijk
 * antwoorden horen beide hun bericht terug te zien, en dat is precies wat een `Y.Array` doet en
 * een `Y.Map` op een teller niet. `opgelost` is juist wél een gewone sleutel: twee mensen die
 * hetzelfde draadje afdoen bedoelen hetzelfde, en de laatste schrijver mag winnen.
 *
 * **Het anker staat niet hier.** Dat is een mark in de tekst die alleen het draad-id draagt, zodat
 * hij meeschuift als er ervoor wordt getypt. Een tekenpositie in deze map zou verouderen bij de
 * eerste wijziging.
 *
 * Kan niet botsen met een veld-id, om dezelfde reden als {@link VALUES}.
 */
export const THREADS = 'draden'

/** De sleutels binnen één draadje. */
const FIELD = 'veldId'
/** Een draadje bij de sectie als geheel, zonder veld (R-73, besluit 8 van 30 oktober 2026). */
const SECTION = 'sectieId'
const QUOTE = 'citaat'
const RESOLVED = 'opgelost'
/**
 * Wie het draadje oploste en wanneer (besluit van 2 november 2026, eisenboek 12.46, ontwerp C.3). Alleen de
 * pod zet ze, nadat de stempel in het stempelregister van de opslag staat; een client die ze zet krijgt ze
 * terug (`apps/runtime/src/threads.ts`). Bij heropenen haalt de pod ze weg. Een draadje van vóór dit besluit
 * heeft ze niet, en wordt gelezen als opgelost zonder naam.
 */
export const RESOLVED_BY = 'opgelostDoor'
export const RESOLVED_AT = 'opgelostOp'
const MESSAGES = 'berichten'
/** En binnen één bericht. */
const PERSON = 'persoon'
const TEXT = 'tekst'
const MOMENT = 'moment'
const MESSAGE_ID = 'id'
/**
 * Een bericht dat zijn schrijver heeft verwijderd (besluit van 2 november 2026, eisenboek 12.45).
 *
 * Een markering op het bericht en geen verwijdering uit de lijst, en dat heeft twee redenen. Een
 * gelijktijdig antwoord van een ander landt in de berichtenlijst; zou wie zijn draadje weggooit de
 * map of de lijst weghalen, dan verdween dat antwoord mee, en dat is precies het bericht van een ander
 * verwijderen. En de pod kan een markering op het bericht van een ander met één sleutel terugzetten
 * (`apps/runtime/src/threadGuard.ts`); een weggehaalde map is alleen als kopie terug te zetten.
 *
 * Additief: een bericht zonder deze sleutel is een bericht, zoals altijd. Het model verandert niet,
 * want `threadsOf` laat een verwijderd bericht weg; een revisie en een naad dragen het dus niet.
 */
export const DELETED = 'verwijderd'

/** Wat er onder een veld-id in de waardenmap kan staan. */
export type Stored =
  | string
  | number
  | boolean
  | null
  | readonly (string | number)[]
  | readonly DocumentVerwijzing[]

export interface Origin {
  readonly door: Person
  readonly gewijzigdOp: Moment
}

/**
 * Een nieuw, leeg sectiedocument.
 *
 * **De opruiming van verwijderde inhoud staat uit** (`gc: false`, valkuil 1 uit
 * ontwerp bijlage C.4). Ruimt Yjs verwijderde inhoud op, dan is een oudere stand
 * niet meer te reconstrueren en valt de attributie van verwijderde tekst weg, en
 * dat is precies wat een revisie en de verschilweergave nodig hebben. Client en
 * server moeten het daarover eens zijn; vanaf M3 zet de pod hem net zo.
 *
 * De prijs is groei, ongeveer evenredig met het bewerkvolume. Het antwoord daarop
 * is geen instelling maar een eindige levensduur per document: generaties met een
 * nulsnapshot (ontwerp 4.6), en die komen in M3.
 *
 * **Een bronsectie krijgt geen document.** Een verwijzing komt uit de
 * dossierdocumenten en reist alleen-lezen in de projectie mee; niemand schrijft
 * hem (R-56, ontwerp 6.8). Een document openen zou een schrijfpad maken waar er
 * geen mag zijn.
 */
export function createSectionDocument(section: Pick<Section, 'type'>): Y.Doc {
  if (section.type === 'bron') {
    throw new Error('een bronsectie heeft geen sectiedocument: verwijzingen komen uit de projectie')
  }
  return new Y.Doc({ gc: false })
}

export function valuesMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap<unknown>(VALUES)
}

/**
 * De auteursmap (ontwerp 6.9). Zie {@link AUTHORS}.
 *
 * Bewust een `Y.Map` en niet een `Y.PermanentUserData`: die laatste hangt bij het maken
 * observers aan het document en die zijn niet op te zeggen, dus wie hem per aanroep bouwt
 * legt er een handler bij die blijft. Eén instantie per document is de bedoeling, en dat is
 * een keuze van de afnemer; de vorm staat hier.
 */
export function authorsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap<Y.Map<unknown>>(AUTHORS)
}

/**
 * Bij welke persoon deze Yjs-client hoort, of niets als hij niet in de map staat.
 *
 * Dezelfde vraag als `Y.PermanentUserData.getUserByClientId`, maar zonder een instantie te
 * bouwen en dus zonder de observers die daaraan hangen. Dat de twee hetzelfde antwoord
 * geven is geen aanname: er staat een test op die de map met deze functie vult en met Yjs
 * uitleest.
 */
export function authorOf(doc: Y.Doc, clientId: number): Person | undefined {
  for (const [person, data] of authorsMap(doc).entries()) {
    const ids = data.get(IDS)
    if (!(ids instanceof Y.Array)) continue
    for (const id of ids as Y.Array<unknown>) {
      if (id === clientId) return person as Person
    }
  }
  return undefined
}

/**
 * Legt vast dat deze Yjs-client bij deze persoon hoort, en maakt een andere aanspraak op
 * dezelfde client ongedaan.
 *
 * Twee dingen die geen detail zijn.
 *
 * **Idempotent.** Staat de koppeling er al, dan gebeurt er niets. `ids` is een `Y.Array` en
 * de map is append-only (valkuil 9): elke duplicaat kost bytes die niet meer weggaan zolang
 * de opruiming uit staat, en dat is per toetsaanslag.
 *
 * **De laatste aanspraak wint niet, deze wint.** Stond de client onder een andere persoon,
 * dan wordt die vermelding weggehaald. Anders zou een client die zichzelf vóór de pod in de
 * map zet zijn eigen zinnen op naam van iemand anders krijgen, en dat is precies wat deze
 * map moet uitsluiten. Wie de map vult hoort dus de partij te zijn die het token heeft
 * gezien; deze functie is het gereedschap en `apps/runtime/src/auteurs.ts` de plek.
 *
 * Geeft terug of er iets is gewijzigd, zodat de aanroeper alleen dan hoeft te melden.
 */
export function recordAuthor(doc: Y.Doc, clientId: number, person: Person): boolean {
  // In één transactie, en dat is geen nettigheid. Yjs voert elke losse `set` als eigen
  // transactie uit en laat de observers er meteen op vuren. `Y.PermanentUserData` observeert
  // deze map en leest bij een nieuwe persoon meteen zijn `ds`-lijst; is die er dan nog niet,
  // dan gooit hij binnen die observer. Bijgekomen bij het bouwen van de rendererkant (M4.11b),
  // waar de attributiecontext er al hangt voordat er iemand wordt genoteerd.
  return doc.transact(() => recordAuthorIn(doc, clientId, person))
}

function recordAuthorIn(doc: Y.Doc, clientId: number, person: Person): boolean {
  const map = authorsMap(doc)
  let changed = false

  for (const [otherPerson, data] of map.entries()) {
    if (otherPerson === person) continue
    const ids = data.get(IDS)
    if (!(ids instanceof Y.Array)) continue
    // Achterstevoren: een verwijdering verschuift de index van alles erachter.
    const list = ids as Y.Array<unknown>
    for (let i = list.length - 1; i >= 0; i -= 1) {
      if (list.get(i) === clientId) {
        list.delete(i, 1)
        changed = true
      }
    }
  }

  let data = map.get(person)
  if (data === undefined) {
    data = new Y.Map<unknown>() as Y.Map<unknown>
    map.set(person, data)
    data.set(IDS, new Y.Array<number>())
    // De verwijderlijsten blijven leeg en horen er toch te staan: `Y.PermanentUserData`
    // haalt ze bij het inlezen van een persoon op en verwacht een `Y.Array`.
    data.set(DELETESETS, new Y.Array<Uint8Array>())
    changed = true
  }

  const ids = data.get(IDS)
  if (!(ids instanceof Y.Array)) {
    throw new Error(`de auteursmap van ${person} draagt geen ids-lijst`)
  }
  const own = ids as Y.Array<unknown>
  for (const id of own) {
    if (id === clientId) return changed
  }
  ;(own as Y.Array<number>).push([clientId])
  return true
}

/** Het fragment van een rich-text-veld, met de veld-id als naam (ontwerp 6.8). */
/**
 * De dradenmap. Zie {@link THREADS}.
 *
 * Net als bij de auteursmap wordt hij hier opgevraagd en niet pas bij het eerste gebruik: Yjs
 * kent het soort van een worteltype niet uit een binnenkomende wijziging, en wie de map nooit
 * heeft opgevraagd krijgt er een naamloos `AbstractType` voor terug.
 */
export function threadsMap(doc: Y.Doc): Y.Map<Y.Map<unknown>> {
  return doc.getMap<Y.Map<unknown>>(THREADS)
}

/** De berichtenlijst van één draadje, of niets als die er niet is. */
export function messagesOf(thread: Y.Map<unknown>): Y.Array<Y.Map<unknown>> | undefined {
  const list = thread.get(MESSAGES)
  return list instanceof Y.Array ? (list as Y.Array<Y.Map<unknown>>) : undefined
}

/** Wat een client meegeeft bij een nieuw bericht: de tekst en een eigen id, en verder niets. */
export interface NewMessage {
  readonly id: string
  readonly tekst: string
}

/**
 * Begint een draadje bij een veld, met zijn eerste bericht (R-73).
 *
 * In één transactie, en dat is dezelfde reden als bij {@link recordAuthor}: Yjs voert elke losse
 * `set` als eigen transactie uit en laat de observers er meteen op vuren. Een draadje dat
 * halverwege in beeld komt heeft dan nog geen bericht, en het model zegt dat zo'n draadje niet
 * bestaat.
 *
 * **Zonder auteur en zonder moment.** Die vult de pod uit het token en van zijn eigen klok
 * (`apps/runtime/src/draden.ts`, R-154). Ze hier alsnog laten meegeven zou de client een auteur
 * laten kiezen, en wie een opmerking op naam van iemand anders kan zetten kan een gesprek
 * verzinnen.
 */
export function beginThread(
  doc: Y.Doc,
  thread: { readonly id: string; readonly citaat: string } & ({ readonly veldId: FieldId } | { readonly sectieId: SectionId }),
  message: NewMessage,
): void {
  doc.transact(() => {
    const map = new Y.Map<unknown>()
    setAnchor(map, thread)
    map.set(QUOTE, thread.citaat)
    map.set(RESOLVED, false)
    const messages = new Y.Array<Y.Map<unknown>>()
    map.set(MESSAGES, messages)
    messages.push([messageMap(message)])
    threadsMap(doc).set(thread.id, map)
  })
}

/** Voegt een bericht aan een bestaand draadje toe; doet niets als het draadje er niet is. */
export function addMessage(doc: Y.Doc, threadId: string, message: NewMessage): boolean {
  const thread = threadsMap(doc).get(threadId)
  if (thread === undefined) return false
  const messages = messagesOf(thread)
  if (messages === undefined) return false
  messages.push([messageMap(message)])
  return true
}

/**
 * Het anker van een draadje: een veld, of een sectie (R-73). Precies één van de twee sleutels; een
 * draadje van vóór de sectie heeft alleen `veldId`, en wordt zo ook gelezen.
 */
function setAnchor(map: Y.Map<unknown>, thread: { readonly veldId?: FieldId | undefined; readonly sectieId?: SectionId | undefined }): void {
  if (thread.veldId !== undefined) map.set(FIELD, thread.veldId)
  else if (thread.sectieId !== undefined) map.set(SECTION, thread.sectieId)
}

function messageMap(message: NewMessage): Y.Map<unknown> {
  const map = new Y.Map<unknown>()
  map.set(MESSAGE_ID, message.id)
  map.set(TEXT, message.tekst)
  return map
}

/**
 * Zet een compleet draadje in een vers document (ontwerp 4.6).
 *
 * Het zaaien van een generatie, voor de draadjes. Anders dan {@link beginThread} draagt dit de
 * auteur en het moment wél: die zijn er al, en de pod hoort ze niet opnieuw vast te stellen. Een
 * gezaaid bericht is dus meteen een geldig bericht, en `threadsOf` laat het niet weg.
 *
 * **Alleen open draadjes gaan mee.** Een opgelost draadje blijft bij de gearchiveerde generatie,
 * en dat is 4.6 letterlijk: over een naad gaan de inhoud, de open opmerkingdraadjes en de rij-id's
 * mee; attributie per zin, verwijderde tekst en opgeloste draadjes niet. Deze functie dwingt dat
 * niet af, want ze weet niet of ze voor een naad of voor iets anders wordt gebruikt; de
 * aanroeper filtert (`apps/runtime/src/generaties.ts`).
 *
 * **Het anker gaat niet mee.** Dat is een mark in de tekst, en de tekst wordt bij een naad
 * opnieuw gezaaid als platte tekst in één paragraaf: de opmaak en de marks van vóór de naad
 * blijven achter. Het citaat is daarom geen nettigheid maar de reden dat een draadje zonder anker
 * leesbaar blijft.
 */
export function seedThread(doc: Y.Doc, thread: Thread): void {
  doc.transact(() => {
    const map = new Y.Map<unknown>()
    // Het anker gaat mee, ook een sectie: anders verdwijnt een sectiedraadje bij de eerste naad.
    setAnchor(map, thread)
    map.set(QUOTE, thread.citaat)
    map.set(RESOLVED, thread.opgelost)
    if (thread.opgelostDoor !== undefined && thread.opgelostOp !== undefined) {
      map.set(RESOLVED_BY, thread.opgelostDoor)
      map.set(RESOLVED_AT, thread.opgelostOp)
    }
    const messages = new Y.Array<Y.Map<unknown>>()
    map.set(MESSAGES, messages)
    messages.push(
      thread.berichten.map((message) => {
        const seeded = new Y.Map<unknown>()
        seeded.set(MESSAGE_ID, message.id)
        seeded.set(TEXT, message.tekst)
        seeded.set(PERSON, message.persoon)
        seeded.set(MOMENT, message.moment)
        return seeded
      }),
    )
    threadsMap(doc).set(thread.id, map)
  })
}

/**
 * Doet een draadje af, of zet het weer open.
 *
 * De berichten blijven staan: oplossen is iets anders dan verwijderen. Een opgelost draadje verdwijnt
 * uit het levende document alleen bij een compactie, met de gearchiveerde generatie (ontwerp 4.6).
 * Verwijderen kan alleen de schrijver van een bericht (`deleteMessage`, eisenboek 12.45).
 */
export function resolveThread(doc: Y.Doc, threadId: string, resolved = true): boolean {
  const thread = threadsMap(doc).get(threadId)
  if (thread === undefined) return false
  thread.set(RESOLVED, resolved)
  return true
}

/**
 * De draadjes van dit document, op id gesorteerd.
 *
 * Gesorteerd, want een `Y.Map` levert zijn sleutels in de orde van invoegen en die verschilt per
 * client; dezelfde inhoud moet in Node en in de browser dezelfde lijst geven (5.9). Een draadje
 * zonder bericht of zonder veld-id komt er niet in: het model zegt dat het niet bestaat, en
 * halve inhoud teruggeven zou die regel bij elke afnemer opnieuw laten controleren.
 */
export function threadsOf(doc: Y.Doc): readonly Thread[] {
  const out: Thread[] = []
  for (const [id, thread] of threadsMap(doc).entries()) {
    const read = readThread(id, thread)
    if (read !== undefined) out.push(read)
  }
  return out.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
}

/** Eén draadje, of niets als het er niet is of onvolledig is. */
export function threadOf(doc: Y.Doc, threadId: string): Thread | undefined {
  const thread = threadsMap(doc).get(threadId)
  return thread === undefined ? undefined : readThread(threadId, thread)
}

function readThread(id: string, thread: Y.Map<unknown>): Thread | undefined {
  const fieldId = thread.get(FIELD)
  const sectionId = thread.get(SECTION)
  const messages = messagesOf(thread)
  // Precies één anker, zoals het model zegt: een veld of een sectie.
  const anchor =
    typeof fieldId === 'string' && sectionId === undefined
      ? { veldId: fieldId as FieldId }
      : typeof sectionId === 'string' && fieldId === undefined
        ? { sectieId: sectionId as SectionId }
        : undefined
  if (anchor === undefined || messages === undefined) return undefined
  const read: Message[] = []
  for (const message of messages) {
    const person = message.get(PERSON)
    const text = message.get(TEXT)
    const moment = message.get(MOMENT)
    const messageId = message.get(MESSAGE_ID)
    // Door zijn schrijver verwijderd: niet meer in het levende document (eisenboek 12.45). In de
    // geschiedenis van Yjs en in een revisie van daarvoor staat het nog.
    if (message.get(DELETED) === true) continue
    // Een bericht dat de pod nog niet heeft gestempeld hoort er nog niet bij: zonder auteur en
    // moment is het geen bericht maar een bewering die onderweg is.
    if (
      typeof person !== 'string' ||
      typeof text !== 'string' ||
      typeof moment !== 'string' ||
      typeof messageId !== 'string'
    ) {
      continue
    }
    read.push({
      id: messageId as Message['id'],
      persoon: person as Person,
      tekst: text,
      moment: moment as Moment,
    })
  }
  if (read.length === 0) return undefined
  const quote = thread.get(QUOTE)
  const resolved = thread.get(RESOLVED) === true
  const resolvedBy = thread.get(RESOLVED_BY)
  const resolvedAt = thread.get(RESOLVED_AT)
  return {
    id: id as Thread['id'],
    ...anchor,
    citaat: typeof quote === 'string' ? quote : '',
    opgelost: resolved,
    // Alleen samen en alleen bij een opgelost draadje, zoals het model zegt; anders opgelost zonder naam.
    ...(resolved && typeof resolvedBy === 'string' && typeof resolvedAt === 'string'
      ? { opgelostDoor: resolvedBy as Person, opgelostOp: resolvedAt as Moment }
      : {}),
    berichten: read,
  }
}

/**
 * De naam van de bronnenmap: de bronnen die de invuller aan een citatie kan hangen (R-50).
 *
 * In het document en niet in een tabel, om dezelfde reden als bij {@link THREADS}: een bron is
 * samenwerkinhoud die de invuller zelf toevoegt terwijl hij schrijft, en een revisie neemt hem
 * mee in zijn Yjs-snapshot.
 *
 * **Eén bron is één waarde, geen genest CRDT-type.** Anders dan een draadje groeit een bron niet
 * doordat twee mensen tegelijk iets toevoegen: hij wordt eenmalig ingevoerd en zelden daarna nog
 * gewijzigd. Twee mensen die dezelfde bron tegelijk bewerken zijn de uitzondering, en voor dat
 * geval is laatste-schrijver-wint op de hele bron geen slechtere uitkomst dan op een van zijn
 * velden; dezelfde afweging als bij `opgelost` in een draadje. De vorm van één bron staat in het
 * model (`Bron`, CSL-JSON); die wordt hier daarom wel gelezen met dat schema, in tegenstelling tot
 * `Thread`: een ondoorzichtige waarde heeft geen eigen velden om stuk voor stuk op `typeof` te
 * toetsen.
 *
 * Kan niet botsen met een veld-id, om dezelfde reden als {@link VALUES}.
 */
export const SOURCES = 'bronnen'

/**
 * De bronnenmap. Zie {@link SOURCES}.
 *
 * Net als bij de dradenmap wordt hij hier opgevraagd en niet pas bij het eerste gebruik, om
 * dezelfde reden: Yjs kent het soort van een worteltype niet uit een binnenkomende wijziging.
 */
export function sourcesMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap<unknown>(SOURCES)
}

/** Voegt een bron toe, of overschrijft een bestaande bron met hetzelfde id. */
export function addSource(doc: Y.Doc, source: Source): void {
  sourcesMap(doc).set(source.id, { ...source })
}

/**
 * Haalt een bron uit dit document. Alleen voor een bron die nergens in de tekst staat: een citatie
 * die naar een weggehaalde bron wijst, valt uit het label en uit de literatuurlijst (R-50). Die
 * grens bewaakt de aanroeper (`useSourcePicker`), want alleen die kent de citaties van de laag.
 */
export function removeSource(doc: Y.Doc, sourceId: string): void {
  sourcesMap(doc).delete(sourceId)
}

/**
 * De bronnen van dit document, op id gesorteerd.
 *
 * Gesorteerd om dezelfde reden als bij {@link threadsOf}: Node en de browser moeten dezelfde
 * lijst geven bij dezelfde inhoud (5.9). Een waarde die niet aan `Bron` voldoet komt er niet in:
 * halve inhoud teruggeven zou die controle bij elke afnemer opnieuw laten uitvoeren.
 */
export function sourcesOf(doc: Y.Doc): readonly Source[] {
  const out: Source[] = []
  for (const value of sourcesMap(doc).values()) {
    const read = Source.safeParse(value)
    if (read.success) out.push(read.data)
  }
  return out.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
}

/** Eén bron, of niets als hij er niet is of niet aan het schema voldoet. */
export function sourceOf(doc: Y.Doc, sourceId: string): Source | undefined {
  const read = Source.safeParse(sourcesMap(doc).get(sourceId))
  return read.success ? read.data : undefined
}

/**
 * De controls waarvan de tekst per teken wordt samengevoegd: een `Y.Text` per veld (R-72).
 *
 * Tot 27 oktober 2026 stond de tekst van deze twee als één waarde in de waardenmap, en een sleutel
 * in een `Y.Map` is laatste-schrijver-wint: twee mensen die tegelijk in hetzelfde tekstveld typten
 * verloren elkaars tekens, terwijl het ontwerp juist belooft dat er dan niets verdwijnt (6.8). Rich
 * text voegde al per teken samen, via zijn fragment; deze twee krijgen nu hetzelfde, zonder opmaak.
 *
 * **Alleen in het document.** De antwoordvorm blijft `waarde` met een gewone tekst
 * (`RESPONSE_SHAPE`), dus de uitgeschreven stand, een revisie, het bericht en alles wat de opslag
 * en het processysteem zien veranderen niet (ontwerp 4.5).
 *
 * **`email`, `url` en `phone` niet.** Korte velden die zelden door twee mensen tegelijk worden
 * bewerkt, en een samengevoegd adres uit twee halve adressen is geen beter antwoord dan het laatst
 * getypte. Ze blijven een waarde in de waardenmap; die grens staat in `docs/dekking.md`.
 *
 * **Een wortel per veld en geen `Y.Text` in de waardenmap.** Een genest type moet iemand aanmaken,
 * en twee clients die dat tegelijk doen zetten elk een eigen `Y.Text` onder dezelfde sleutel: de
 * map houdt er één en de tekens in de andere zijn weg, precies het verlies dat hier wordt
 * opgelost. Een wortel bestaat in elk document onder zijn naam, dus er valt niets aan te maken.
 * In een herhaalgroep kan dat niet (elke rij zou dezelfde naam hebben); daar legt `addRowTo` de
 * `Y.Text` aan bij het maken van de rij, zoals het fragment van een rich-text-kolom.
 *
 * **Een worteltype heeft één soort.** Yjs weigert `getXmlFragment` op een naam die al als `Y.Text`
 * is opgevraagd, en omgekeerd; en een wijziging van de verkeerde soort die binnenkomt op een naam
 * die al een soort heeft, landt als onzin in dat type. Wie een veld schrijft, kiest de route dus op
 * het control en nooit op de veld-id alleen.
 */
export const MERGED_TEXT_CONTROLS = ['text', 'textarea'] as const satisfies readonly Control[]

/** Of de tekst van dit control per teken wordt samengevoegd. Zie {@link MERGED_TEXT_CONTROLS}. */
export function isMergedText(control: Control): boolean {
  return (MERGED_TEXT_CONTROLS as readonly Control[]).includes(control)
}

/** De tekst van een tekstveld op sectieniveau, met de veld-id als naam. Zie {@link MERGED_TEXT_CONTROLS}. */
export function fieldText(doc: Y.Doc, fieldId: FieldId): Y.Text {
  return doc.getText(fieldId)
}

/** De tekst van een tekstveld op sectieniveau als gewone string. */
export function textOf(doc: Y.Doc, fieldId: FieldId): string {
  return fieldText(doc, fieldId).toString()
}

/**
 * Welke tekens er tussen twee teksten zijn veranderd: één aaneengesloten stuk.
 *
 * Een invoerveld meldt na elke toetsaanslag alleen de nieuwe tekst. Die als geheel terugschrijven
 * zou de tekst van een medeschrijver wissen die intussen binnenkwam, dus wat naar de `Y.Text` gaat
 * is alleen het verschil: vanaf `index` gaan er `deleted` tekens weg en komt `inserted` erbij.
 */
export interface TextChange {
  readonly index: number
  readonly deleted: number
  readonly inserted: string
}

/**
 * Het verschil tussen twee teksten (zie {@link TextChange}), of niets als ze gelijk zijn.
 *
 * **De cursor beslist bij herhaalde tekens.** Wie in "aa" vooraan een "a" typt, levert "aaa", en
 * het gemeenschappelijke begin zegt dan dat de nieuwe "a" achteraan staat. Dezelfde uitkomst als
 * tekst, maar niet als plaats: een medeschrijver die intussen tussen de twee letters typte zou zijn
 * tekst aan de verkeerde kant terugzien. Met de cursor erbij (waar die na de wijziging staat) kan
 * het begin niet voorbij de plek waar de invoeging begon.
 *
 * **Nooit midden in een tekenpaar.** Een emoji of een ander teken buiten het basisvlak is in een
 * JavaScript-string twee code-eenheden, en `Y.Text` telt ook zo. Een grens tussen die twee zou een
 * half teken laten staan.
 */
export function textChange(before: string, after: string, cursor?: number): TextChange | undefined {
  if (before === after) return undefined
  let start = 0
  const shortest = Math.min(before.length, after.length)
  while (start < shortest && before.charCodeAt(start) === after.charCodeAt(start)) start += 1
  if (cursor !== undefined) {
    const grown = Math.max(0, after.length - before.length)
    start = Math.max(0, Math.min(start, cursor - grown))
  }
  if (start > 0 && isHighSurrogate(before.charCodeAt(start - 1))) start -= 1
  let endBefore = before.length
  let endAfter = after.length
  while (endBefore > start && endAfter > start && before.charCodeAt(endBefore - 1) === after.charCodeAt(endAfter - 1)) {
    endBefore -= 1
    endAfter -= 1
  }
  if (endBefore < before.length && isLowSurrogate(before.charCodeAt(endBefore))) {
    endBefore += 1
    endAfter += 1
  }
  return { index: start, deleted: endBefore - start, inserted: after.slice(start, endAfter) }
}

function isHighSurrogate(code: number): boolean {
  return code >= 0xd800 && code <= 0xdbff
}

function isLowSurrogate(code: number): boolean {
  return code >= 0xdc00 && code <= 0xdfff
}

/** Past een {@link TextChange} toe op een `Y.Text`, in één transactie. Doet niets zonder verschil. */
export function applyTextChange(text: Y.Text, change: TextChange | undefined, transactionOrigin?: unknown): void {
  if (change === undefined) return
  const action = (): void => {
    if (change.deleted > 0) text.delete(change.index, change.deleted)
    if (change.inserted !== '') text.insert(change.index, change.inserted)
  }
  if (text.doc === null) action()
  else text.doc.transact(action, transactionOrigin)
}

/**
 * Zet een `Y.Text` op deze tekst, met alleen het verschil als wijziging.
 *
 * Het pad voor wie de nieuwe tekst als geheel heeft: het zaaien, de voorvulling uit de querystring
 * (R-37), het processysteem dat een veld vult (R-54) en de bewaking die het terugzet (R-58). Wat er
 * al klopt blijft staan, dus een gelijke tekst kost geen bytes (valkuil 1).
 */
export function replaceText(text: Y.Text, next: string): void {
  applyTextChange(text, textChange(text.toString(), next))
}

/**
 * De tekst van een tekstveld zetten, met zijn herkomst, in één transactie (zoals `setValue`).
 *
 * Leeg is leeg: wie een veld leegmaakt laat een lege `Y.Text` met een herkomst achter, en
 * `answerOf` maakt daar hetzelfde antwoord van als vroeger een `null` in de waardenmap (R-26).
 */
export function setText(doc: Y.Doc, fieldId: FieldId, value: string, origin: Origin): void {
  doc.transact(() => {
    replaceText(fieldText(doc, fieldId), value)
    recordOriginIn(valuesMap(doc), fieldId, origin)
  })
}

/**
 * De tekst van een tekstkolom in deze rij, of niets als hij er niet is.
 *
 * Niets betekent: deze rij is gemaakt zonder dat die kolom als tekst bekend was. Aanleggen doet de
 * aanroeper, buiten het renderen, zoals bij {@link rowFragment}.
 */
export function rowText(row: Y.Map<unknown>, fieldId: FieldId): Y.Text | undefined {
  const value = row.get(fieldId)
  return value instanceof Y.Text ? value : undefined
}

/** Een ontbrekende tekst in een rij aanleggen; alleen buiten het renderen aanroepen. */
export function createRowText(row: Y.Map<unknown>, fieldId: FieldId): Y.Text {
  const existing = rowText(row, fieldId)
  if (existing !== undefined) return existing
  const created = new Y.Text()
  row.set(fieldId, created)
  return created
}

/** De tekst van een tekstkolom in een rij zetten, met zijn herkomst, in één transactie. */
export function setRowTextIn(row: Y.Map<unknown>, rowFieldId: FieldId, value: string, origin: Origin): void {
  const action = (): void => {
    replaceText(createRowText(row, rowFieldId), value)
    recordOriginIn(row, rowFieldId, origin)
  }
  if (row.doc === null) action()
  else row.doc.transact(action)
}

export function richtextFragment(doc: Y.Doc, fieldId: FieldId): Y.XmlFragment {
  return doc.getXmlFragment(fieldId)
}

/** De rijen van een herhaalgroep, met de veld-id van de groep als naam. */
export function rowsList(doc: Y.Doc, fieldId: FieldId): Y.Array<Y.Map<unknown>> {
  return doc.getArray<Y.Map<unknown>>(fieldId)
}

/**
 * Waarde en herkomst gaan in één transactie de map in. Los van elkaar zou een
 * medeschrijver kort een waarde zien zonder auteur, en een revisie die op dat
 * moment sluit zou een antwoord zonder herkomst vastleggen; het schema weigert dat
 * en dan valt de overdracht om.
 */
function write(map: Y.Map<unknown>, fieldId: FieldId, value: Stored, origin: Origin, options?: WriteOptions): void {
  const action = (): void => {
    map.set(fieldId, value)
    if (options?.exactMoment === true) setOriginKeys(map, fieldId, origin)
    else recordOriginIn(map, fieldId, origin)
  }
  map.doc?.transact(action)
}

/** Hoe een schrijfactie met een waarde de herkomst behandelt. */
export interface WriteOptions {
  /**
   * Auteur en moment bij elke schrijfactie, ook als het veld al op naam van deze auteur staat.
   *
   * Voor het akkoordveld (R-19, R-89): de stempel toont bij Ja meteen het moment van die keuze,
   * en niet even dat van een eerdere Nee tot de pod het rechtzet. Een akkoord is één klik en geen
   * reeks aanslagen, dus dit kost geen groei. De pod stempelt een akkoord ook zelf tot op de
   * milliseconde (`apps/runtime/src/origin.ts`).
   */
  readonly exactMoment?: boolean
}

export function setValue(
  doc: Y.Doc,
  fieldId: FieldId,
  value: string | number | boolean | null,
  origin: Origin,
  options?: WriteOptions,
): void {
  write(valuesMap(doc), fieldId, value, origin, options)
}

/**
 * De gekozen opties van een meerkeuzeveld of een aanvinklijst, of de trefwoorden
 * van een `tag` (R-15).
 *
 * De waarden staan als gewone array onder één sleutel, zoals ontwerp 6.8 de map
 * beschrijft. Gevolg: twee mensen die op hetzelfde moment een andere optie
 * aanvinken overschrijven elkaar, want een sleutel in een `Y.Map` is
 * laatste-schrijver-wint. Dat is geen dataverlies over een beurt heen, wel een
 * verloren aanvinking binnen enkele seconden. Of dat zo blijft of dat een
 * meerwaardig veld een genest CRDT-type krijgt, hoort bij de samenwerklaag van M3;
 * de drie gelijktijdigheidsgevallen die M3 aantoonbaar moet halen (ontwerp 11.1)
 * noemen dit geval niet.
 */
export function setValues(
  doc: Y.Doc,
  fieldId: FieldId,
  values: readonly (string | number)[],
  origin: Origin,
): void {
  if (new Set(values).size !== values.length) {
    throw new Error(`dezelfde waarde staat er twee keer in bij ${fieldId}`)
  }
  write(valuesMap(doc), fieldId, [...values], origin)
}

/** Een bijlageveld bewaart verwijzingen en nooit het bestand (U25, R-199). */
export function setDocuments(
  doc: Y.Doc,
  fieldId: FieldId,
  documents: readonly DocumentVerwijzing[],
  origin: Origin,
): void {
  write(valuesMap(doc), fieldId, documents.map((d) => ({ ...d })), origin)
}

/**
 * Alleen de herkomst vastleggen, zonder een waarde.
 *
 * Dit is het pad voor een rich-text-veld: de tekst zelf staat in het fragment en
 * wordt door de samenwerklaag geschreven, maar een revisie vraagt per antwoord wie
 * het als laatste raakte en wanneer (R-97, R-188). Zonder deze schrijfactie heeft een
 * rich-text-veld geen herkomst en valt zijn antwoord uit de stand, en dan blijft een
 * `verplicht`-regel erop staan terwijl er tekst is.
 *
 * Alleen bij een eigen wijziging aanroepen: wie de tekst van een medeschrijver
 * binnenkrijgt is niet degene die hem schreef.
 */
export function recordOrigin(doc: Y.Doc, fieldId: FieldId, origin: Origin): void {
  doc.transact(() => recordOriginIn(valuesMap(doc), fieldId, origin))
}

/**
 * De twee herkomstsleutels in deze map: de waardenmap, of de map van een rij.
 *
 * Alleen als het veld nog geen herkomst heeft of als die van een ander is (besluit van 29 oktober
 * 2026, ontwerp 4.6). Dit loopt bij elke eigen aanslag, en een herkomst per aanslag kost bytes die
 * niet meer weggaan (valkuil 1). Het moment houdt de pod per minuut bij met zijn eigen klok; zou
 * de renderer dat ook met de klok van de browser doen, dan zetten een scheve klok en die van de pod
 * elkaar rond de minuutgrens bij elke aanslag recht.
 */
export function recordOriginIn(map: Y.Map<unknown>, fieldId: string, origin: Origin): void {
  if (map.get(`${fieldId}${BY_SUFFIX}`) === origin.door && typeof map.get(`${fieldId}${AT}`) === 'string') return
  setOriginKeys(map, fieldId, origin)
}

function setOriginKeys(map: Y.Map<unknown>, fieldId: string, origin: Origin): void {
  map.set(`${fieldId}${BY_SUFFIX}`, origin.door)
  map.set(`${fieldId}${AT}`, origin.gewijzigdOp)
}

/** De herkomst van één veld, of niets als er nog niets is geschreven. */
export function originOf(doc: Y.Doc, fieldId: FieldId): Origin | undefined {
  return originIn(valuesMap(doc), fieldId)
}

/** De herkomst van één veld in deze map: de waardenmap, of de map van een rij. */
export function originIn(map: Y.Map<unknown>, fieldId: string): Origin | undefined {
  const by = map.get(`${fieldId}${BY_SUFFIX}`)
  const changedAt = map.get(`${fieldId}${AT}`)
  if (typeof by !== 'string' || typeof changedAt !== 'string') return undefined
  return { door: by, gewijzigdOp: changedAt }
}

/**
 * De rij-primitieven werken op de lijst en niet op het document.
 *
 * Een control krijgt daarmee precies de herhaalgroep waar hij over gaat, en niet de
 * andere velden van de sectie. De varianten met een document erbij staan eronder en
 * leunen hierop; die zijn er voor de aanroeper die alleen een veld-id heeft.
 */

/**
 * Een rij erbij, met een rij-id dat de rij houdt zolang hij bestaat (R-40).
 *
 * **Een rich-text-kolom krijgt een eigen fragment in de rijmap.** Ontwerp 6.8 legt
 * vier vormen vast en noemt dit geval niet, terwijl het model rich text in een rij
 * toestaat (`RijVeld`) en het voorbeeldaanvraagformulier van M0 het gebruikt: de
 * werkpakkettentabel heeft een beschrijving per rij. Een fragment op de veld-id in
 * het sectiedocument kan hier niet, want dat is voor elke rij dezelfde naam en dan
 * schrijven alle rijen in één tekst. Een `Y.XmlFragment` als waarde in de rijmap
 * houdt de vorm heel: het document bevat de fragmenten, de rij is een map daarin.
 *
 * De fragmenten worden bij het maken van de rij aangelegd en niet bij het eerste
 * lezen: een document mag niet veranderen doordat iemand ernaar kijkt, want die
 * wijziging reist naar alle medeschrijvers.
 */
export function addRowTo(
  list: Y.Array<Y.Map<unknown>>,
  rowId: RowId,
  fragmentFields: readonly FieldId[] = [],
  textFields: readonly FieldId[] = [],
): Y.Map<unknown> {
  const row = new Y.Map<unknown>()
  const action = () => {
    row.set('rijId', rowId)
    // Achteraan in de volgorde (R-40). Twee medeschrijvers die tegelijk een rij toevoegen krijgen
    // dezelfde sleutel; het rij-id beslist dan, bij iedereen gelijk (`orderedRows`).
    row.set(ROW_ORDER, keyBetween(lastKey(list), undefined))
    for (const fieldId of fragmentFields) row.set(fieldId, new Y.XmlFragment())
    // Een tekstkolom krijgt zijn `Y.Text` om dezelfde reden hier en niet bij de eerste toets: twee
    // medeschrijvers die hem elk zelf aanmaken overschrijven elkaars tekst (zie `MERGED_TEXT_CONTROLS`).
    for (const fieldId of textFields) row.set(fieldId, new Y.Text())
    list.push([row])
  }
  // In één transactie, zodat een medeschrijver nooit een halve rij ziet.
  if (list.doc === null) action()
  else list.doc.transact(action)
  return row
}

/**
 * Het fragment van een rich-text-kolom in deze rij, of niets als het er niet is.
 *
 * Niets betekent: deze rij is gemaakt zonder dat die kolom bekend was. Aanleggen doet
 * de aanroeper, buiten het renderen.
 */
export function rowFragment(row: Y.Map<unknown>, fieldId: FieldId): Y.XmlFragment | undefined {
  const value = row.get(fieldId)
  return value instanceof Y.XmlFragment ? value : undefined
}

/** Een ontbrekend fragment aanleggen; alleen buiten het renderen aanroepen. */
export function createRowFragment(row: Y.Map<unknown>, fieldId: FieldId): Y.XmlFragment {
  const existing = rowFragment(row, fieldId)
  if (existing !== undefined) return existing
  const createNew = new Y.XmlFragment()
  row.set(fieldId, createNew)
  return createNew
}

/**
 * Een rij eruit, op zijn rij-id en niet op zijn plaats in de lijst: wie op index
 * verwijdert haalt bij een gelijktijdige invoeging de verkeerde rij weg.
 */
export function removeRowFrom(list: Y.Array<Y.Map<unknown>>, rowId: RowId, origin?: unknown): void {
  const place = list.toArray().findIndex((row) => row.get('rijId') === rowId)
  if (place < 0) return
  transactIn(list, () => list.delete(place, 1), origin)
}

/**
 * De sleutel in een rijmap met haar plaats in de volgorde (R-40, besluit van 31 oktober 2026,
 * ontwerp 6.8). Zie `orderKey.ts` voor waarom een sleutel en niet de plaats in de lijst.
 *
 * **Met een `@` ervoor**, zoals de herkomstsleutels: een veld-id bevat er nooit een, dus het kan
 * met geen kolom botsen, en elke lezer die de waarden van een rij doorloopt slaat sleutels met een
 * `@` al over (`rowValuesOf` in `state.ts`). De pod stempelt hem niet (`findTargets` in
 * `apps/runtime/src/origin.ts`): een rij draagt zelf geen herkomst, haar waarden wel.
 */
export const ROW_ORDER = '@volgorde'

function orderKeyOf(row: Y.Map<unknown> | undefined): string | undefined {
  const value = row?.get(ROW_ORDER)
  return isOrderKey(value) ? value : undefined
}

function rowIdOf(row: Y.Map<unknown>): string {
  const id = row.get('rijId')
  return typeof id === 'string' ? id : ''
}

/**
 * De rijen in de volgorde waarin de invuller ze ziet, en waarin de stand en een revisie ze lezen.
 *
 * Op sleutel, en bij een gelijke sleutel op rij-id: met een gewone stringvergelijking en niet met
 * `localeCompare`, zodat elke client en de pod precies hetzelfde sorteren. **Een rij zonder sleutel
 * staat achteraan, in de volgorde van de lijst.** Dat is een rij uit een document van vóór de
 * sleutel, of een die een oudere renderer toevoegde; beide kwamen achteraan de lijst binnen, dus
 * achteraan is ook waar ze horen. Zijn er alleen zulke rijen, dan is het gewoon de lijst.
 */
export function orderedRows(list: Y.Array<Y.Map<unknown>>): Y.Map<unknown>[] {
  const keyed: { row: Y.Map<unknown>; key: string; id: string }[] = []
  const bare: Y.Map<unknown>[] = []
  for (const row of list.toArray()) {
    const key = orderKeyOf(row)
    if (key === undefined) bare.push(row)
    else keyed.push({ row, key, id: rowIdOf(row) })
  }
  keyed.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  return [...keyed.map((entry) => entry.row), ...bare]
}

/** De rij-id's in de getoonde volgorde. */
export function orderedRowIds(list: Y.Array<Y.Map<unknown>>): RowId[] {
  return orderedRows(list)
    .map((row) => row.get('rijId'))
    .filter((id): id is RowId => typeof id === 'string')
}

/** De sleutel van de laatste rij, na het geven van een sleutel aan wie er nog geen heeft. */
function lastKey(list: Y.Array<Y.Map<unknown>>): string | undefined {
  giveKeys(list)
  return orderKeyOf(orderedRows(list).at(-1))
}

/**
 * Elke rij zonder sleutel krijgt er een, achter de laatste, in de volgorde van de lijst.
 *
 * Deterministisch: twee clients die dit tegelijk doen op dezelfde stand schrijven dezelfde
 * sleutels, en dan maakt het niet uit wiens schrijfactie wint.
 */
function giveKeys(list: Y.Array<Y.Map<unknown>>): void {
  let last: string | undefined
  for (const row of orderedRows(list)) {
    const key = orderKeyOf(row)
    if (key !== undefined) {
      last = key
      continue
    }
    last = keyBetween(last, undefined)
    row.set(ROW_ORDER, last)
  }
}

/** Alle rijen een nieuwe sleutel in de huidige volgorde; alleen als twee buren dezelfde hebben. */
function renumber(rows: readonly Y.Map<unknown>[]): void {
  let last: string | undefined
  for (const row of rows) {
    last = keyBetween(last, undefined)
    row.set(ROW_ORDER, last)
  }
}

function transactIn(list: Y.Array<Y.Map<unknown>>, action: () => void, origin: unknown): void {
  if (list.doc === null) action()
  else list.doc.transact(action, origin)
}

/**
 * Een rij verplaatsen naar de plek vóór `before`, of naar het eind als die er niet is (R-40, R-72).
 *
 * **De rijmap blijft liggen; alleen haar sleutel verandert.** Zo houdt de rij haar rij-id, haar
 * cellen en elke `Y.Text` en elk fragment erin: een medeschrijver die tegelijk in die rij typt raakt
 * niets kwijt, en verwijzingen, correcties (R-201) en revisies blijven op dezelfde rij-id wijzen.
 * De nieuwe sleutel komt uit de sleutels van de nieuwe buren en niet uit een plaatsnummer, want
 * een plaatsnummer verschuift onder een gelijktijdige invoeging.
 *
 * Geeft `false` als er niets verandert: een onbekende rij, vóór zichzelf, of de plek waar hij al
 * staat.
 */
export function moveRowTo(list: Y.Array<Y.Map<unknown>>, rowId: RowId, before: RowId | undefined, origin?: unknown): boolean {
  const order = orderedRows(list)
  const from = order.findIndex((row) => row.get('rijId') === rowId)
  if (from < 0 || before === rowId) return false
  if (before !== undefined && !order.some((row) => row.get('rijId') === before)) return false
  const next = order[from + 1]
  if (before === undefined ? from === order.length - 1 : next?.get('rijId') === before) return false
  const row = order[from]
  if (row === undefined) return false
  transactIn(
    list,
    () => {
      giveKeys(list)
      const place = (rest: readonly Y.Map<unknown>[]): number =>
        before === undefined ? rest.length : rest.findIndex((candidate) => candidate.get('rijId') === before)
      let rest = orderedRows(list).filter((candidate) => candidate !== row)
      let low = orderKeyOf(rest[place(rest) - 1])
      let high = orderKeyOf(rest[place(rest)])
      if (low !== undefined && high !== undefined && low >= high) {
        // Twee buren met dezelfde sleutel (twee rijen die tegelijk werden toegevoegd): eerst opnieuw nummeren.
        renumber(orderedRows(list))
        rest = orderedRows(list).filter((candidate) => candidate !== row)
        low = orderKeyOf(rest[place(rest) - 1])
        high = orderKeyOf(rest[place(rest)])
      }
      row.set(ROW_ORDER, keyBetween(low, high))
    },
    origin,
  )
  return true
}

/** Een rij één plek omhoog (`-1`) of omlaag (`1`); `false` aan de rand. */
export function moveRowBy(list: Y.Array<Y.Map<unknown>>, rowId: RowId, delta: -1 | 1, origin?: unknown): boolean {
  const order = orderedRowIds(list)
  const from = order.indexOf(rowId)
  if (from < 0) return false
  if (delta === -1) {
    const before = order[from - 1]
    return before === undefined ? false : moveRowTo(list, rowId, before, origin)
  }
  if (from === order.length - 1) return false
  return moveRowTo(list, rowId, order[from + 2], origin)
}

/**
 * Verplaatsen en verwijderen met ongedaan maken, van de samenwerklaag (valkuil 5).
 *
 * Een `Y.UndoManager` op de lijst van deze herhaalgroep die **alleen de eigen verplaatsingen en
 * verwijderingen** volgt: zijn bereik is de lijst met alles erin, dus zonder een eigen herkomst
 * zou hij ook het typen in een cel oppakken, en dat is de stapel van die tekst zelf
 * (`textBinding.ts`, de editor van rich text). Ongedaan maken van een verplaatsing zet de vorige
 * sleutel van die ene rij terug; wat een medeschrijver intussen in die rij typte blijft staan.
 * Ongedaan maken van een verwijdering zet de rij terug met haar rij-id en haar inhoud, want de
 * opruiming staat uit (valkuil 1).
 *
 * Toevoegen staat er bewust niet in: ongedaan maken van een toevoeging haalt de rij weg, ook met
 * wat een ander er intussen in schreef.
 *
 * Elke handeling is een eigen stap (`captureTimeout: 0`): twee keer snel omhoog is twee keer terug.
 */
export interface RowOrderUndo {
  move(rowId: RowId, before: RowId | undefined): boolean
  moveBy(rowId: RowId, delta: -1 | 1): boolean
  remove(rowId: RowId): void
  undo(): void
  redo(): void
  canUndo(): boolean
  canRedo(): boolean
  /** Meldt een verandering van de stapels; geeft de afmelding terug. */
  onChange(hook: () => void): () => void
  destroy(): void
}

export function rowOrderUndo(list: Y.Array<Y.Map<unknown>>): RowOrderUndo {
  const origin = { herhaalgroep: 'eigen' }
  const manager = new Y.UndoManager(list, { trackedOrigins: new Set([origin]), captureTimeout: 0 })
  return {
    move: (rowId, before) => moveRowTo(list, rowId, before, origin),
    moveBy: (rowId, delta) => moveRowBy(list, rowId, delta, origin),
    remove: (rowId) => removeRowFrom(list, rowId, origin),
    undo: () => {
      manager.undo()
    },
    redo: () => {
      manager.redo()
    },
    canUndo: () => manager.canUndo(),
    canRedo: () => manager.canRedo(),
    onChange: (hook) => {
      manager.on('stack-item-added', hook)
      manager.on('stack-item-popped', hook)
      manager.on('stack-cleared', hook)
      return () => {
        manager.off('stack-item-added', hook)
        manager.off('stack-item-popped', hook)
        manager.off('stack-cleared', hook)
      }
    },
    destroy: () => manager.destroy(),
  }
}

/** De rij met dit id, of niets. */
export function rowById(list: Y.Array<Y.Map<unknown>>, rowId: RowId): Y.Map<unknown> | undefined {
  return list.toArray().find((row) => row.get('rijId') === rowId)
}

/** Een waarde in een rij, met dezelfde herkomstsleutels als in de waardenmap. */
export function setRowValueIn(
  row: Y.Map<unknown>,
  rowFieldId: FieldId,
  value: Stored,
  origin: Origin,
  options?: WriteOptions,
): void {
  write(row, rowFieldId, value, origin, options)
}

export function addRow(
  doc: Y.Doc,
  fieldId: FieldId,
  rowId: RowId,
  fragmentFields: readonly FieldId[] = [],
  textFields: readonly FieldId[] = [],
): Y.Map<unknown> {
  return addRowTo(rowsList(doc, fieldId), rowId, fragmentFields, textFields)
}

export function removeRow(doc: Y.Doc, fieldId: FieldId, rowId: RowId): void {
  removeRowFrom(rowsList(doc, fieldId), rowId)
}

export function setRowValue(
  doc: Y.Doc,
  fieldId: FieldId,
  rowId: RowId,
  rowFieldId: FieldId,
  value: Stored,
  origin: Origin,
): void {
  const row = rowById(rowsList(doc, fieldId), rowId)
  if (row === undefined) throw new Error(`rij ${rowId} bestaat niet in ${fieldId}`)
  setRowValueIn(row, rowFieldId, value, origin)
}

/**
 * De platte tekst van een rich-text-veld.
 *
 * **Platte tekst en niet de opmaak, en dat is een keuze met een reden aan twee
 * kanten.** De uitgeschreven stand in een revisie moet zelfstandig leesbaar zijn
 * (ontwerp 4.5), en de teken- en woordlimiet rekenen erover: de teller van R-12
 * en de harde woordgrens van R-13 tellen woorden en geen elementnamen. Zou hier de
 * XML-vorm staan, dan telde de evaluator `<p>` mee en klopte de grens niet met wat
 * de invuller ziet.
 *
 * De opmaak gaat daarmee niet verloren. Een revisie bewaart naast de uitgeschreven
 * stand per sectie een Yjs-snapshot (ontwerp 4.5), en daarin staat het fragment
 * met alles erin: opmaak, formule, figuur en referentie (R-14). Precies daarom
 * bewaart een revisie die twee dingen naast elkaar.
 *
 * Blokken worden met een regeleinde gescheiden, want zonder scheiding plakken het
 * laatste woord van de ene alinea en het eerste van de volgende aan elkaar en telt
 * de woordenteller één woord te weinig.
 *
 * **Een regeleinde alleen tussen blokken, nooit binnen één alinea.** Een atoomknoop zonder
 * tekst (`figuur`, `referentie`) levert zelf een lege bijdrage; staat hij tussen tekst in
 * dezelfde alinea (`referentie` is inline), dan zou een regeleinde aan weerskanten een zin
 * uiteenrijten: "zie eerder onderzoek [referentie]." werd zo "zie eerder onderzoek \n\n.".
 * `textFrom` onderscheidt daarom een alinea (kinderen zijn tekst en eventueel een inline
 * atoomknoop, aaneengeregen zonder scheidingsteken) van een blokniveau (kinderen zijn zelf
 * alinea's of andere blokken, met een regeleinde ertussen) — puur aan de boomvorm: een knoop
 * met een `Y.XmlText`-kind is een alinea, een knoop zonder is een blok. Geen kennis van
 * knooptypen nodig en dus geen tweede, met de hand bijgehouden lijst naast het editorschema.
 */
export function plainTextOf(doc: Y.Doc, fieldId: FieldId): string {
  return plainTextOfFragment(richtextFragment(doc, fieldId))
}

/**
 * De tekens van één tekstknoop, zonder opmaak.
 *
 * Niet `toString()`: die schrijft een mark als element om de tekst heen (`<bold>meet</bold>`), en dan
 * telden de teller en de woordgrens die namen mee, precies wat hierboven is uitgesloten. De delta
 * draagt de tekst en de opmaak apart; alleen de tekst telt.
 */
function textOfNode(node: Y.XmlText): string {
  return (node.toDelta() as readonly { readonly insert?: unknown }[]).map((op) => (typeof op.insert === 'string' ? op.insert : '')).join('')
}

/**
 * Dezelfde tekst, maar dan van een fragment dat de aanroeper al heeft.
 *
 * De teller onder een rich-text-veld gebruikt deze functie en de uitgeschreven
 * stand van een revisie ook. Dat is geen gemak maar een eis: het getal dat de
 * invuller ziet en het getal waarop de woordgrens indienen tegenhoudt moeten
 * hetzelfde zijn (R-12, R-13).
 */
export function plainTextOfFragment(fragment: Y.XmlFragment): string {
  return textFrom(fragment)
}

function textFrom(node: Y.XmlFragment | Y.XmlElement | Y.XmlText | Y.XmlHook): string {
  if (node instanceof Y.XmlText) return textOfNode(node)
  if (node instanceof Y.XmlHook) return ''
  const children = node.toArray()
  const isParagraph = children.some((child) => child instanceof Y.XmlText)
  return children.map((child) => textFrom(child)).join(isParagraph ? '' : '\n')
}
